import { inject, Injectable, Injector, Type } from '@angular/core';
import { FormKey } from '@common/components/my-account/my-account-sidepanel/my-account-sidepanel.model';
import { SidepanelBase, SidepanelCrudBase, SidepanelService } from 'mbs-ui-kit';
import { from, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

type SidepanelToggleOptions = {
  open?: boolean;
  tab?: FormKey;
};

@Injectable({ providedIn: 'root' })
export class MyAccountSidepanelService {
  private readonly injector = inject(Injector);
  private readonly sidepanelService = inject(SidepanelService);

  public accessor sidepanel: SidepanelBase | SidepanelCrudBase<boolean>;

  toggleSidepanel(options?: SidepanelToggleOptions): Observable<SidepanelBase | SidepanelCrudBase<boolean>> {
    const { open = true, tab } = options || {};

    return this.loadComponent().pipe(
      switchMap((component) => this.toggleByType(component, open)),
      tap((panel) => tab && setTimeout(() => (panel as SidepanelCrudBase<boolean>).tabset.select(tab), 0))
    );
  }

  private loadComponent(): Observable<Type<SidepanelBase | SidepanelCrudBase<boolean>>> {
    return from(import('./my-account-sidepanel.component')).pipe(map(({ MyAccountSidepanelComponent }) => MyAccountSidepanelComponent));
  }

  private toggleByType<T extends SidepanelBase | SidepanelCrudBase<boolean>>(type: Type<T>, open: boolean): Observable<T> {
    const panel = this.sidepanelService.get(type) || this.sidepanelService.add(type, null, this.injector);
    const panelByType = open ? this.sidepanelService.openByType(type) : this.sidepanelService.closeByType(type);

    // Re-read on every toggle: AppComponent removes the panel on page change, and the tabs subscribe to the current instance's `close`
    this.sidepanel = panel;

    return panelByType.pipe(
      take(1),
      map(() => panel)
    );
  }
}
